import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material';
import { MotionConfig, AnimatePresence } from 'framer-motion';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { createAppTheme } from './theme';
import Layout from './components/Layout';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import VerifyEmail from './components/auth/VerifyEmail';
import OAuthCallback from './components/auth/OAuthCallback';
import Dashboard from './components/Dashboard';
import BudgetList from './components/budgets/BudgetList';
import TransactionList from './components/transactions/TransactionList';
import ExpenseAnalytics from './components/reports/ExpenseAnalytics';
import Settings from './components/settings/Settings';
import Support from './components/Support';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import PrivateRoute from './components/PrivateRoute';
import AuthLayout from './components/auth/AuthLayout';
import MarketingPage from './components/MarketingPage';
import { SettingsProvider, useSettings } from './contexts/SettingsContext';
import { BudgetProvider } from './contexts/BudgetContext';
import { LoadingProvider } from './contexts/LoadingContext';
import { NavigationProvider } from './contexts/NavigationContext';
import { TransactionProvider } from './contexts/TransactionContext';
import { SessionProvider, useSession } from './contexts/SessionContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import PageTransition from './components/PageTransition';
import { CustomDragLayer } from './components/CustomDragLayer';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import ErrorBoundary from './components/common/ErrorBoundary';
import { suppressResizeObserverErrors } from './utils/resizeObserverWorkaround';

// Component for public routes that redirects authenticated users to dashboard
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useSession();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Redirect authenticated users to dashboard
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const RoutesWithTransitions = () => {
  const location = useLocation();
  
  return (
    <ErrorBoundary>
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
        {/* Marketing page for unauthenticated users */}
        <Route path="/" element={
          <PublicRoute>
            <PageTransition><MarketingPage /></PageTransition>
          </PublicRoute>
        } />

        {/* Auth routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={
            <PublicRoute>
              <PageTransition><Login /></PageTransition>
            </PublicRoute>
          } />
          <Route path="/register" element={
            <PublicRoute>
              <PageTransition><Register /></PageTransition>
            </PublicRoute>
          } />
          <Route path="/verify-email" element={<PageTransition><VerifyEmail /></PageTransition>} />
          <Route path="/forgot-password" element={
            <PublicRoute>
              <PageTransition><ForgotPassword /></PageTransition>
            </PublicRoute>
          } />
          <Route path="/reset-password" element={<PageTransition><ResetPassword /></PageTransition>} />
        </Route>

        {/* Public legal pages */}
        <Route path="/terms" element={<PageTransition><TermsOfService /></PageTransition>} />
        <Route path="/privacy" element={<PageTransition><PrivacyPolicy /></PageTransition>} />

        {/* OAuth callback route */}
        <Route path="/auth/callback" element={<OAuthCallback />} />

        {/* Protected routes wrapped with BudgetProvider, LoadingProvider, NavigationProvider, TransactionProvider, and OnboardingProvider */}
        <Route element={
          <BudgetProvider>
            <LoadingProvider>
              <NavigationProvider>
                <TransactionProvider>
                  <OnboardingProvider>
                    <PrivateRoute><Layout /></PrivateRoute>
                  </OnboardingProvider>
                </TransactionProvider>
              </NavigationProvider>
            </LoadingProvider>
          </BudgetProvider>
        }>
          <Route path="/dashboard" element={<PageTransition><Dashboard /></PageTransition>} />
          <Route path="/budgets" element={<PageTransition><BudgetList /></PageTransition>} />
          <Route path="/transactions" element={<PageTransition><TransactionList /></PageTransition>} />
          <Route path="/analytics" element={<PageTransition><ExpenseAnalytics /></PageTransition>} />
          <Route path="/settings" element={<PageTransition><Settings /></PageTransition>} />
          <Route path="/support" element={<PageTransition><Support /></PageTransition>} />
          <Route path="/terms-of-service" element={<PageTransition><TermsOfService /></PageTransition>} />
          <Route path="/privacy-policy" element={<PageTransition><PrivacyPolicy /></PageTransition>} />
        </Route>

        {/* Redirect all other routes appropriately */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AnimatePresence>
    </ErrorBoundary>
  );
};

const QueryThemeHandler: React.FC = () => {
  const location = useLocation();
  const { darkMode, toggleDarkMode } = useSettings();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const themeParam = params.get('theme');
    
    if (themeParam === 'dark' && !darkMode) {
      toggleDarkMode();
    } else if (themeParam === 'light' && darkMode) {
      toggleDarkMode();
    }
  }, [location.search, darkMode, toggleDarkMode]);

  return null;
};

const ThemedApp: React.FC = () => {
  const { darkMode } = useSettings();
  const theme = createAppTheme(darkMode);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <MotionConfig reducedMotion="user">
        <DndProvider backend={HTML5Backend}>
          <Router>
            <SessionProvider>
              <NotificationProvider>
                <QueryThemeHandler />
                <RoutesWithTransitions />
                <CustomDragLayer />
              </NotificationProvider>
            </SessionProvider>
          </Router>
        </DndProvider>
      </MotionConfig>
    </ThemeProvider>
  );
};

const App: React.FC = () => {
  // Comprehensive ResizeObserver error suppression
  useEffect(() => {
    const cleanup = suppressResizeObserverErrors();
    return cleanup;
  }, []);

  return (
    <SettingsProvider>
      <ThemedApp />
    </SettingsProvider>
  );
};

export default App;
